import { useCallback, useEffect, useRef } from 'react';

export const useChartEvents = (
  chartRef,
  chartContainerRef,
  containerId,
  reportType,
  handleSelection,
  syncGroupId,
  resetZoom
) => {
  const isProcessingRef = useRef(false);

  // Добавляем диагностическое логирование
  console.log(`useChartEvents: Инициализация для контейнера ${containerId}`, {
    hasChartRef: !!chartRef?.current,
    hasContainerRef: !!chartContainerRef?.current,
    reportType,
    syncGroupId
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Проверяем, находится ли фокус на элементе ввода или редактирования
      const isInputFocused = document.activeElement && 
        (document.activeElement.tagName === 'INPUT' || 
         document.activeElement.tagName === 'TEXTAREA' || 
         document.activeElement.isContentEditable);
      
      // Если фокус на элементе ввода, не обрабатываем клавиши
      if (isInputFocused) return;
      
      // Сбрасываем масштаб по Escape
      if (event.key === 'Escape') {
        console.log('BaseChart: Сброс зума по клавише Escape');
        resetZoom();
        event.preventDefault();
        return;
      }
      
      // Сбрасываем масштаб по пробелу
      if (event.key === ' ') {
        console.log('BaseChart: Сброс зума по клавише пробел');
        resetZoom();
        event.preventDefault();
        return;
      }
      
      // Сбрасываем масштаб по Ctrl+0 (или Cmd+0 на Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === '0') {
        console.log('BaseChart: Сброс зума по Ctrl+0');
        resetZoom();
        event.preventDefault();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [resetZoom]);

  // Функция для обработки выделения точки
  const showPointTooltip = useCallback((pointIndex, datasetIndex = 0) => {
    if (!chartRef.current) return;
    
    try {
      const chart = chartRef.current;
      
      // Проверяем, существует ли указанный набор данных
      if (!chart.data || !chart.data.datasets || !chart.data.datasets[datasetIndex]) {
        console.warn(`BaseChart: Набор данных с индексом ${datasetIndex} не найден`);
        datasetIndex = 0;
      }
      
      // Проверяем наличие метаданных для набора данных
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || !meta.data || !meta.data[pointIndex]) {
        console.warn(`BaseChart: Точка ${pointIndex} не найдена в наборе данных ${datasetIndex}`);
        return;
      }
      
      // Скрываем все активные подсказки
      if (chart.tooltip) {
        chart.tooltip.setActiveElements([], { datasetIndex, index: pointIndex });
      }
      
      // Получаем элемент точки данных
      const element = meta.data[pointIndex];
      
      // Создаем и отображаем подсказку
      chart.tooltip.setActiveElements([{ datasetIndex, index: pointIndex }], {
        x: element.x,
        y: element.y
      });
      
      // Обновляем график без анимации
      chart.update('none');
    } catch (error) {
      console.error('BaseChart: Ошибка при отображении подсказки:', error);
    }
  }, [chartRef]);

  // Функция для добавления выделения точки на график
  const addHighlightPoint = useCallback((pointIndex, datasetIndex = 0) => {
    if (!chartRef.current) return;
    
    try {
      const chart = chartRef.current;
      
      // Проверяем, существует ли указанный набор данных
      if (!chart.data || !chart.data.datasets || !chart.data.datasets[datasetIndex]) {
        console.warn(`BaseChart: Набор данных с индексом ${datasetIndex} не найден`);
        datasetIndex = 0;
      }
      
      // Проверяем наличие метаданных для набора данных
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || !meta.data || !meta.data[pointIndex]) {
        console.warn(`BaseChart: Точка ${pointIndex} не найдена в наборе данных ${datasetIndex}`);
        return;
      }
      
      // Сбрасываем текущие таймеры если они есть
      if (chart._highlightResetTimer) {
        clearTimeout(chart._highlightResetTimer);
        chart._highlightResetTimer = null;
      }
      
      if (chart._pulseAnimationTimer) {
        clearTimeout(chart._pulseAnimationTimer);
        chart._pulseAnimationTimer = null;
      }
      
      // Сохраняем оригинальные настройки для восстановления
      if (!chart._originalPointStyles) {
        chart._originalPointStyles = {};
        chart.data.datasets.forEach((dataset, i) => {
          chart._originalPointStyles[i] = {
            radius: dataset.pointRadius,
            backgroundColor: dataset.pointBackgroundColor,
            borderColor: dataset.pointBorderColor
          };
        });
      }
      
      // Удаляем старую фиксированную подсказку если есть
      if (chart._fixedTooltipElement) {
        chart._fixedTooltipElement.remove();
        chart._fixedTooltipElement = null;
      }
      
      // Для графика расхода топлива (fuel) подготавливаем только выбранный набор данных
      const isMultiLineChart = chart.data.datasets.length > 1;
      const isFuelChart = reportType === 'fuel';
      
      // Сначала сбрасываем стили всех точек на всех наборах данных
      chart.data.datasets.forEach((ds, i) => {
        const dsLength = ds.data ? ds.data.length : 0;
        
        // Для не активных наборов данных в графиках "fuel" скрываем все точки
        if (isFuelChart && isMultiLineChart && i !== datasetIndex) {
          ds.pointRadius = Array(dsLength).fill(0);
          ds.pointBackgroundColor = Array(dsLength).fill('transparent');
          ds.pointBorderColor = Array(dsLength).fill('transparent');
        } else {
          if (i === datasetIndex) {
            // Создаем массивы для радиусов и цветов точек только для выбранного набора
            const pointRadiusArray = Array(dsLength).fill(0); // Все точки невидимы
            pointRadiusArray[pointIndex] = 10; // Только выделенная точка видима и увеличенного размера
            
            const pointBackgroundColorArray = Array(dsLength).fill('transparent');
            pointBackgroundColorArray[pointIndex] = 'rgba(255, 0, 0, 0.9)'; // Более яркая красная точка для выделения
            
            const pointBorderColorArray = Array(dsLength).fill('transparent');
            pointBorderColorArray[pointIndex] = '#fff';
            
            // Применяем новые стили к выбранному набору данных
            ds.pointRadius = pointRadiusArray;
            ds.pointBackgroundColor = pointBackgroundColorArray;
            ds.pointBorderColor = pointBorderColorArray;
          }
        }
      });
      
      // Получаем элемент точки
      const pointElement = meta.data[pointIndex];
      
      // Только если элемент найден
      if (pointElement && pointElement.element) {
        // Используем CSS-класс для анимации
        pointElement.element.classList.add('chart-highlighted-point');
      }
      
      // Показываем подсказку один раз для выделенной точки
      chart.tooltip.setActiveElements([{ datasetIndex, index: pointIndex }], {
        x: pointElement.x,
        y: pointElement.y - 10
      });
      
      // Обновляем настройки подсказки, чтобы она работала при наведении
      chart.options.plugins.tooltip.enabled = true;
      chart.options.plugins.tooltip.position = 'nearest';
      
      // Восстанавливаем стандартное поведение подсказок
      if (chart.options.plugins.tooltip.callbacks._persistentTooltip) {
        delete chart.options.plugins.tooltip.callbacks._persistentTooltip;
      }
      
      // Восстанавливаем оригинальный метод отрисовки, если он был изменен
      if (chart._originalDraw) {
        chart.draw = chart._originalDraw;
        chart._originalDraw = null;
      }
      
      // Сохраняем информацию о текущей выделенной точке
      chart._currentHighlightedPoint = {
        datasetIndex,
        pointIndex
      };
      
      // Обновляем график без анимации для мгновенного отображения
      chart.update('none');
      
      console.log(`BaseChart: Добавлено выделение точки индекс=${pointIndex}, набор=${datasetIndex}`);
    } catch (error) {
      console.error('BaseChart: Ошибка при добавлении выделения точки:', error);
    }
  }, [chartRef, reportType]);

  // Функция для сброса выделения точек
  const resetPointHighlight = useCallback(() => {
    if (!chartRef.current) return;
    
    try {
      const chart = chartRef.current;
      const originalStyles = chart._originalPointStyles;
      
      // Останавливаем анимацию пульсации
      if (chart._pulseAnimationTimer) {
        clearTimeout(chart._pulseAnimationTimer);
        chart._pulseAnimationTimer = null;
      }
      
      // Останавливаем таймер автоматического сброса
      if (chart._highlightResetTimer) {
        clearTimeout(chart._highlightResetTimer);
        chart._highlightResetTimer = null;
      }
      
      // Удаляем фиксированную подсказку если есть
      if (chart._fixedTooltipElement) {
        chart._fixedTooltipElement.remove();
        chart._fixedTooltipElement = null;
      }
      
      // Очищаем информацию о текущей выделенной точке
      if (chart._currentHighlightedPoint) {
        // Удаляем CSS-класс анимации, если есть элемент
        const { datasetIndex, pointIndex } = chart._currentHighlightedPoint;
        try {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (meta && meta.data && meta.data[pointIndex] && meta.data[pointIndex].element) {
            meta.data[pointIndex].element.classList.remove('chart-highlighted-point');
          }
        } catch (e) {
          // Игнорируем ошибки при поиске элемента
        }
        
        chart._currentHighlightedPoint = null;
      }
      
      // Восстанавливаем оригинальный метод отрисовки, если был изменен
      if (chart._originalDraw) {
        chart.draw = chart._originalDraw;
        chart._originalDraw = null;
      }
      
      // Удаляем обработчик _persistentTooltip
      if (chart.options.plugins.tooltip.callbacks._persistentTooltip) {
        delete chart.options.plugins.tooltip.callbacks._persistentTooltip;
      }
      
      // Очищаем фиксированную позицию подсказки
      if (chart._fixedTooltipPosition) {
        chart._fixedTooltipPosition = null;
      }
      
      if (originalStyles) {
        // Восстанавливаем оригинальные стили точек для всех наборов данных
        chart.data.datasets.forEach((dataset, i) => {
          if (originalStyles[i]) {
            dataset.pointRadius = originalStyles[i].radius;
            dataset.pointBackgroundColor = originalStyles[i].backgroundColor;
            dataset.pointBorderColor = originalStyles[i].borderColor;
          }
        });
      }
      
      // Удаляем аннотацию вертикальной линии если есть
      if (chart.options.plugins.annotation && 
          chart.options.plugins.annotation.annotations && 
          chart.options.plugins.annotation.annotations.verticalLine) {
        delete chart.options.plugins.annotation.annotations.verticalLine;
      }
      
      // Обновляем график без анимации
      chart.update('none');
      
      // Скрываем подсказку
      if (chart.tooltip) {
        chart.tooltip.setActiveElements([], {});
        chart.update('none');
      }
      
      console.log(`BaseChart: Сброшено выделение точек`);
    } catch (error) {
      console.error('BaseChart: Ошибка при сбросе выделения точки:', error);
    }
  }, [chartRef]);

  // Функция для сброса выделения точек на всех графиках
  const resetAllPointHighlights = useCallback(() => {
    // Отправляем глобальное событие для сброса выделения на всех графиках
    document.dispatchEvent(new CustomEvent('resetAllPointHighlights', {}));
    
    // Локально сбрасываем выделение на текущем графике
    resetPointHighlight();
  }, [resetPointHighlight]);

  // Функция синхронизации выделения точки
  const syncPointSelection = useCallback((timestamp, pointIndex, datasetIndex = 0) => {
    // Проверка, что timestamp существует
    if (!timestamp) return;
    
    // Отправляем событие для синхронизации выделения точки
    const syncEvent = new CustomEvent('chartPointSelected', {
      detail: {
        sourceContainerId: containerId,
        timestamp,
        pointIndex,
        datasetIndex,
        label: window.selectedChartPoint?.label,
        value: window.selectedChartPoint?.value,
        reportType,
        priority: 'high'
      },
      bubbles: false
    });
    
    document.dispatchEvent(syncEvent);
    
    // Добавляем вызов функции showPointTooltip для отображения подсказки
    if (chartRef.current && pointIndex >= 0) {
      showPointTooltip(pointIndex, datasetIndex);
    }
  }, [containerId, reportType, chartRef, showPointTooltip]);

  // Функция для применения выделения к графику
  const applySelectionToChart = useCallback((selectionData) => {
    if (!chartRef.current || !selectionData) {
      console.log(`BaseChart: Не удалось применить выделение к графику ${containerId} - отсутствует chartRef или данные выделения`);
      return;
    }
    
    try {
      console.log(`BaseChart: Применяем выделение к графику ${containerId}:`, selectionData);
      const chart = chartRef.current;
      
      // Для выделения по диапазону времени
      if (selectionData.startDate && selectionData.endDate) {
        const startTime = new Date(selectionData.startDate).getTime();
        const endTime = new Date(selectionData.endDate).getTime();
        
        console.log(`BaseChart: Диапазон времени для выделения ${new Date(startTime).toISOString()} - ${new Date(endTime).toISOString()}`);
        
        // Если у нас есть plugin zoom, используем его для выделения
        if (chart.scales && chart.scales.x) {
          // Применяем выделение через Chart.js zoom plugin
          if (chart.zoom && typeof chart.zoom.zoomScale === 'function') {
            console.log(`BaseChart: Применяем zoom.zoomScale для графика ${containerId}`);
            
            const zoomOptions = {
              min: startTime,
              max: endTime
            };
            
            // Убедимся, что анимация отключена для мгновенного применения
            const originalAnimation = chart.options.animation;
            chart.options.animation = false;
            
            // Применяем зум
            chart.zoom.zoomScale('x', zoomOptions);
            
            // Восстанавливаем настройки анимации
            setTimeout(() => {
              chart.options.animation = originalAnimation;
            }, 100);
            
            console.log(`BaseChart: Выделение успешно применено через zoom.zoomScale`);
          }
          // Устанавливаем min/max напрямую
          else if (chart.scales.x.options) {
            console.log(`BaseChart: Применяем выделение через scales.x.options для графика ${containerId}`);
            
            const currentMin = chart.scales.x.min;
            const currentMax = chart.scales.x.max;
            console.log(`BaseChart: Текущие границы графика: ${currentMin} - ${currentMax}`);
            
            // Устанавливаем новые границы
            chart.scales.x.options.min = startTime;
            chart.scales.x.options.max = endTime;
            
            // Принудительное обновление без анимации
            chart.update('none');
            
            console.log(`BaseChart: Новые границы графика: ${chart.scales.x.min} - ${chart.scales.x.max}`);
          }
          
          // Сохраняем диапазон выделения в атрибуты для отладки
          chart.canvas.setAttribute('data-selection-start', selectionData.startDate);
          chart.canvas.setAttribute('data-selection-end', selectionData.endDate);
          chart.canvas.setAttribute('data-selection-applied', 'true');
          
          console.log(`BaseChart: Выделение успешно применено к графику ${containerId}`);
        } else {
          console.log(`BaseChart: У графика нет шкалы X для применения выделения`);
        }
      }
      // Для выделения по индексам
      else if (selectionData.startIndex !== undefined && selectionData.endIndex !== undefined) {
        console.log(`BaseChart: Применяем выделение по индексам ${selectionData.startIndex} - ${selectionData.endIndex}`);
        
        if (chart.scales && chart.scales.x) {
          chart.options.scales.x.min = selectionData.startIndex;
          chart.options.scales.x.max = selectionData.endIndex;
          
          chart.update('none');
          
          chart.canvas.setAttribute('data-selection-start-index', selectionData.startIndex);
          chart.canvas.setAttribute('data-selection-end-index', selectionData.endIndex);
          chart.canvas.setAttribute('data-selection-applied', 'true');
          
          console.log(`BaseChart: Выделение по индексам успешно применено к графику ${containerId}`);
        }
      }
      
      // Отправляем событие о том, что выделение было применено
      const event = new CustomEvent('chartSelectionApplied', {
        detail: {
          containerId,
          selectionData,
          success: true,
          timestamp: Date.now()
        }
      });
      document.dispatchEvent(event);
      
    } catch (error) {
      console.error('BaseChart: Ошибка при применении выделения к графику:', error);
      
      // Отправляем событие о неудачном применении выделения
      const event = new CustomEvent('chartSelectionApplied', {
        detail: {
          containerId,
          selectionData,
          success: false,
          error: error.message,
          timestamp: Date.now()
        }
      });
      document.dispatchEvent(event);
    }
  }, [chartRef, containerId]);

  // Функция для получения точек данных графика
  const getDataPoints = useCallback(() => {
    if (!chartRef.current || !chartRef.current.data || !chartRef.current.data.datasets || !chartRef.current.data.labels) {
      return [];
    }
    
    const dataset = chartRef.current.data.datasets[0];
    const labels = chartRef.current.data.labels;
    
    if (!dataset || !dataset.data || !labels) {
      return [];
    }
    
    return dataset.data.map((y, index) => {
      let x = labels[index];
      if (!(x instanceof Date) && typeof x === 'string') {
        try {
          x = new Date(x);
        } catch (e) {
          console.log(`BaseChart: Не удалось преобразовать метку ${x} в дату`);
        }
      }
      
      return { x, y, index };
    });
  }, [chartRef]);

  // Улучшенная функция обработки выделения
  const enhancedHandleSelection = useCallback((startIndex, endIndex) => {
    if (startIndex === undefined || endIndex === undefined) return;
    
    // Получаем данные точек
    const dataPoints = getDataPoints();
    if (!dataPoints || dataPoints.length === 0) return;
    
    // Получаем начальную и конечную точки выделения
    const startPoint = dataPoints[Math.max(0, startIndex)];
    const endPoint = dataPoints[Math.min(dataPoints.length - 1, endIndex)];
    
    if (!startPoint || !endPoint) return;
    
    // Конвертируем индексы в даты (для временных графиков)
    const startDate = startPoint.x instanceof Date ? startPoint.x : 
                      typeof startPoint.x === 'number' ? new Date(startPoint.x) : null;
    const endDate = endPoint.x instanceof Date ? endPoint.x : 
                    typeof endPoint.x === 'number' ? new Date(endPoint.x) : null;
    
    // Создаем объект с данными выделения
    const selectionData = {
      startDate,
      endDate,
      startIndex,
      endIndex
    };
    
    console.log(`BaseChart: Отправка уведомления о выделении, syncGroupId: ${syncGroupId}`);
    
    // Вызываем оригинальный handleSelection
    if (handleSelection) {
      handleSelection(startIndex, endIndex);
    }
    
    // Устанавливаем выделение через ChartSyncManager
    if (window.chartSyncManager) {
      window.chartSyncManager.saveSelectionFromChart(containerId);
      
      window.chartSyncManager.syncSelection(containerId, {
        min: startDate ? startDate.getTime() : startIndex,
        max: endDate ? endDate.getTime() : endIndex
      });
    }
    
    // Отправляем событие о выделении напрямую родителю, если это разделенный контейнер
    if (containerId && containerId.includes('-')) {
      const baseContainerId = containerId.split('-')[0];
      
      console.log(`BaseChart: Отправляем событие выделения к родительскому контейнеру ${baseContainerId}`);
      document.dispatchEvent(new CustomEvent('childGraphSelection', {
        detail: {
          childId: containerId,
          parentId: baseContainerId,
          syncGroupId,
          selectionData
        }
      }));
    }
    
    // Отправляем дополнительное событие для обработки другими компонентами
    document.dispatchEvent(new CustomEvent('chartSelectionChanged', {
      detail: {
        sourceContainerId: containerId,
        selectionData,
        syncGroupId: syncGroupId || 'default'
      }
    }));
    
  }, [getDataPoints, containerId, syncGroupId, handleSelection]);

  // Функция для отправки события выделения в SplitScreenContainer
  const notifySelectionChanged = useCallback((selectionData) => {
    // Получаем контейнер с графиком
    const container = chartContainerRef.current?.closest('.split-screen-container');
    if (!container) {
      console.warn(`BaseChart: Не удалось найти контейнер для уведомления о выделении`);
      return;
    }
    
    const containerSyncGroupId = container.getAttribute('data-sync-group');
    console.log(`BaseChart: Отправка уведомления о выделении, syncGroupId: ${containerSyncGroupId || 'нет'}`);
    
    // Проверяем, есть ли у контейнера атрибут data-sync-selection
    const shouldSync = container.getAttribute('data-sync-selection') !== 'false';
    if (!shouldSync) {
      console.log(`BaseChart: Синхронизация выделений отключена для контейнера ${containerId}`);
      return;
    }
    
    // Если есть API через __reactInstance, используем его
    if (container.__reactInstance && container.__reactInstance.notifySelectionChanged) {
      console.log(`BaseChart: Уведомляем SplitScreenContainer о выделении через API`);
      container.__reactInstance.notifySelectionChanged(selectionData);
      
      // Также отправляем DOM-событие для обработки другими компонентами
      const containerEvent = new CustomEvent('selectionChanged', {
        detail: {
          selectionData,
          containerId: container.id || container.getAttribute('data-container-id')
        }
      });
      container.dispatchEvent(containerEvent);
      return;
    }
    
    // Если нет прямого API, отправляем событие
    console.log(`BaseChart: Уведомляем о выделении через событие graphSelectionChanged`);
    const event = new CustomEvent('graphSelectionChanged', {
      detail: {
        sourceContainerId: containerId,
        selectionData,
        syncGroupId: containerSyncGroupId,
        timestamp: Date.now()
      }
    });
    document.dispatchEvent(event);
    
    // Дополнительно, отправляем специальное событие для локальной обработки в контейнере
    const containerEvent = new CustomEvent('selectionChanged', {
      detail: {
        selectionData,
        containerId: container.id || container.getAttribute('data-container-id')
      }
    });
    container.dispatchEvent(containerEvent);
    
  }, [chartContainerRef, containerId]);

  // Функция обработки клика на графике
  const handleChartClick = useCallback((event) => {
    // Проверяем, что у нас есть ссылка на график и его данные
    if (!chartRef.current || !chartRef.current.data || !chartRef.current.data.labels) {
      console.log('BaseChart: График не инициализирован или отсутствуют данные');
      return;
    }

    // Получаем элементы под курсором
    const clickedElements = chartRef.current.getElementsAtEventForMode(
      event.nativeEvent, 
      'nearest', 
      { intersect: true }, 
      false
    );
    
    // Получаем родительский контейнер графика и активируем его
    const container = chartContainerRef.current?.closest('.split-screen-container');
    
    if (container) {
      // Сначала снимаем активность со всех контейнеров
      document.querySelectorAll('.split-screen-container[data-active="true"]')
        .forEach(el => {
          if (el && document.body.contains(el)) {
            el.setAttribute('data-active', 'false');
            el.classList.remove('active-container');
          }
        });
      
      // Активируем контейнер
      container.setAttribute('data-active', 'true');
      container.classList.add('active-container');
      
      console.log(`BaseChart: Активирован контейнер ${containerId} при клике по графику`);
    }

    // Если элементы не найдены, прерываем дальнейшую обработку
    if (!clickedElements || clickedElements.length === 0) {
      console.log(`BaseChart: Клик не попал в данные графика`);
      return;
    }

    // Если пользователь кликнул на точку графика
    try {
      const clickedElement = clickedElements[0];
      const datasetIndex = clickedElement.datasetIndex;
      const pointIndex = clickedElement.index;
      
      // Обработка Shift-клика для создания выделения диапазона
      if (event.nativeEvent.shiftKey && chartRef.current.lastClickIndex !== undefined) {
        console.log(`BaseChart: Обнаружен Shift-клик, создаем выделение от ${chartRef.current.lastClickIndex} до ${pointIndex}`);
        
        const startIndex = Math.min(chartRef.current.lastClickIndex, pointIndex);
        const endIndex = Math.max(chartRef.current.lastClickIndex, pointIndex);
        
        enhancedHandleSelection(startIndex, endIndex);
        return;
      }
      
      // Сначала сбрасываем предыдущее выделение на всех графиках
      resetAllPointHighlights();
      
      console.log(`BaseChart: Клик на точке индекс=${pointIndex}, набор данных=${datasetIndex}`);
      
      // Получаем данные о точке
      const dataset = chartRef.current.data.datasets[datasetIndex];
      const label = chartRef.current.data.labels[pointIndex];
      const value = dataset.data[pointIndex];
      
      // Определяем временную метку
      let timestamp = null;
      
      if (label instanceof Date) {
        timestamp = label.getTime();
      } else if (typeof label === 'string') {
        try {
          timestamp = new Date(label).getTime();
        } catch (e) {
          // Игнорируем ошибки преобразования
        }
      } else if (typeof label === 'number') {
        timestamp = label;
      }
      
      // Сохраняем выделенную точку глобально для синхронизации
      window.selectedChartPoint = {
        timestamp,
        pointIndex,
        value,
        containerId,
        label,
        reportType,
        datasetIndex
      };
      
      // Добавляем выделение с фиксированной подсказкой
      addHighlightPoint(pointIndex, datasetIndex);
      
      // Синхронизируем выделение точки с другими графиками
      syncPointSelection(timestamp, pointIndex, datasetIndex);
      
      // Запоминаем последний выбранный индекс для выделения диапазона
      chartRef.current.lastClickIndex = pointIndex;
      
      // Сбрасываем текущее выделение масштаба если оно есть
      if (chartRef.current.canvas.hasAttribute('data-selection-applied')) {
        console.log(`BaseChart: Сброс текущего выделения масштаба`);
        resetZoom();
      }
    } catch (error) {
      console.error('BaseChart: Ошибка при выделении точки:', error);
    }
  }, [chartRef, chartContainerRef, containerId, reportType, enhancedHandleSelection, resetAllPointHighlights, 
      addHighlightPoint, syncPointSelection, resetZoom]);

  // Функция экспорта графика
  const exportChart = useCallback(() => {
    if (chartRef.current) {
      const link = document.createElement('a');
      link.download = `chart-${containerId}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = chartRef.current.toBase64Image();
      link.click();
    }
  }, [chartRef, containerId]);

  // Функция переключения синхронизации
  const toggleSyncMode = useCallback(() => {
    if (window.chartSyncManager) {
      const isEnabled = window.chartSyncManager.toggleSync();
      console.log(isEnabled ? 'Синхронизация включена' : 'Синхронизация отключена');
      return isEnabled;
    }
    return false;
  }, []);

  // Event listeners
  useEffect(() => {
    // Обработчик события выделения точки на другом графике
    const handlePointSelected = (event) => {
      const { sourceContainerId, timestamp, datasetIndex: sourceDatasetIndex = 0 } = event.detail;
      
      // Не обрабатываем событие от самого себя
      if (sourceContainerId === containerId) return;
      
      // Если уже обрабатываем событие, пропускаем
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      
      requestAnimationFrame(() => {
        try {
          if (!chartRef.current || !chartRef.current.data || !chartRef.current.data.labels) {
            isProcessingRef.current = false;
            return;
          }
          
          // Ищем соответствующую точку по времени в этом графике
          const labels = chartRef.current.data.labels;
          let matchedIndex = -1;
          let minTimeDiff = Infinity;
          
          for (let i = 0; i < labels.length; i++) {
            const label = labels[i];
            let labelTime = null;
            
            if (label instanceof Date) {
              labelTime = label.getTime();
            } else if (typeof label === 'string' && /^\d/.test(label)) {
              try {
                labelTime = new Date(label).getTime();
              } catch (e) { /* Ignore */ }
            } else if (typeof label === 'number') {
              labelTime = label;
            }
            
            // Если нашли точное совпадение
            if (labelTime === timestamp) {
              matchedIndex = i;
              break;
            }
            
            // Если нет точного совпадения, находим ближайшую точку
            if (labelTime && typeof labelTime === 'number' && typeof timestamp === 'number') {
              const timeDiff = Math.abs(labelTime - timestamp);
              if (timeDiff < minTimeDiff) {
                minTimeDiff = timeDiff;
                matchedIndex = i;
              }
            }
          }
          
          // Если нашли подходящую точку
          if (matchedIndex !== -1) {
            const targetDatasetIndex = reportType === 'fuel' ? (sourceDatasetIndex || 0) : 0;
            addHighlightPoint(matchedIndex, targetDatasetIndex);
          }
        } finally {
          isProcessingRef.current = false;
        }
      });
    };
    
    // Обработчик сброса всех выделений
    const handleResetAllHighlights = () => {
      resetPointHighlight();
    };
    
    // Обработчик применения выделения графика
    const handleApplyGraphSelection = (event) => {
      const { selectionData, targetContainerId } = event.detail;
      
      const isForThisContainer = !targetContainerId || targetContainerId === containerId;
      
      if (isForThisContainer && chartRef.current && selectionData) {
        console.log(`BaseChart: Применяем выделение к графику ${containerId}`);
        applySelectionToChart(selectionData);
      }
    };
    
    // Обработчик применения выделения к контейнеру
    const handleApplySelectionToContainer = (event) => {
      const { containerId: targetId, selectionData } = event.detail;
      
      if (targetId === containerId && chartRef.current && selectionData) {
        console.log(`BaseChart: Получено глобальное событие выделения для контейнера ${containerId}`);
        applySelectionToChart(selectionData);
      }
    };
    
    // Добавляем обработчики событий
    document.addEventListener('chartPointSelected', handlePointSelected);
    document.addEventListener('resetAllPointHighlights', handleResetAllHighlights);
    document.addEventListener('applyGraphSelection', handleApplyGraphSelection);
    document.addEventListener('applySelectionToContainer', handleApplySelectionToContainer);
    
    return () => {
      document.removeEventListener('chartPointSelected', handlePointSelected);
      document.removeEventListener('resetAllPointHighlights', handleResetAllHighlights);
      document.removeEventListener('applyGraphSelection', handleApplyGraphSelection);
      document.removeEventListener('applySelectionToContainer', handleApplySelectionToContainer);
    };
  }, [containerId, chartRef, reportType, addHighlightPoint, resetPointHighlight, applySelectionToChart]);

  return {
    exportChart,
    toggleSyncMode,
    handleChartClick,
    getDataPoints,
    handleSelection: enhancedHandleSelection,
    notifySelectionChanged,
    applySelectionToChart,
    addHighlightPoint,
    resetPointHighlight,
    resetAllPointHighlights,
    syncPointSelection,
    showPointTooltip
  };
}; 